import { useMemo } from 'react';
import { t } from 'i18next';
import { PhoneInput } from '@Components/inputs/Phone';
import { OnboardingFormFields } from '@Types/types.ts';
import { useBoundStore } from '@Store/useBoundStore.ts';
import { useGetCountries } from '@Hooks/api/useGetCountries';
import type { Dispatch, SetStateAction } from 'react';
import type { IPhoneForm } from '@Pages/happyFlowPages/phone/phoneForm/phoneForm.types.ts';
import type { ExtendedUseFormReturn } from '@Hooks/index';

import type { RegionCode } from 'google-libphonenumber';

interface IPhoneFormProps {
  formMethods: ExtendedUseFormReturn<IPhoneForm>;
  isSelectMenuDisabled: boolean;
  setLocalSelectedIsoCode?: Dispatch<SetStateAction<RegionCode>>;
}

export const PhoneForm = (props: IPhoneFormProps) => {
  const { phoneCountryCode } = useBoundStore((state) => state.customer.customerData);
  const { data: countriesDataResponse } = useGetCountries(true);
  const {
    isSelectMenuDisabled,
    setLocalSelectedIsoCode,
    formMethods: { getValues, getFieldState, setValue, trigger, setError },
  } = props;

  const handlePhoneValidationStatus = (isValid: boolean) => {
    if (!isValid && getFieldState(OnboardingFormFields.PHONE_NUMBER).isTouched) {
      setError(OnboardingFormFields.PHONE_NUMBER, { message: t('enErrors:phone') });
    }
    setValue(OnboardingFormFields.PHONE_NUMBER_VALIDATION_STATUS, isValid);
    trigger(OnboardingFormFields.PHONE_NUMBER_VALIDATION_STATUS);
  };

  const handleIsoCodeChange = (isoCode: RegionCode) => {
    setLocalSelectedIsoCode?.(isoCode);
  };

  const handleBlur = () => {
    if (
      !getValues(OnboardingFormFields.PHONE_NUMBER_VALIDATION_STATUS) &&
      getFieldState(OnboardingFormFields.PHONE_NUMBER).isTouched
    ) {
      setTimeout(() => {
        setError(OnboardingFormFields.PHONE_NUMBER, { message: t('enErrors:phone') });
      }, 100);
    }
  };

  const countriesFilterList = useMemo(() => {
    if (!countriesDataResponse) return ['US'];
    return countriesDataResponse.countries.map((country) => country.code);
  }, [countriesDataResponse]);

  return (
    <PhoneInput
      onIsoCodeChange={handleIsoCodeChange}
      initialIsoCode={phoneCountryCode}
      isSelectMenuDisabled={isSelectMenuDisabled}
      onBlur={handleBlur}
      onPhoneValidationStatusChange={handlePhoneValidationStatus}
      filteredCountries={countriesFilterList as RegionCode[]}
    />
  );
};
